import React from 'react';
import Page, { Grid, GridColumn } from '@atlaskit/page';
import { Box, xcss } from '@atlaskit/primitives';
import Breadcrumbs from '@atlassian/jira-breadcrumbs/src/breadcrumb-renderer/index.tsx';
import PageHeader from '@atlassian/jira-common-components-page-header';
import { fg } from '@atlassian/jira-feature-gating';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import AppTiles from '@atlassian/jira-marketplace-app-tiles/src/ui/main.tsx';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';
import { JsmPageInteractiveEvent } from '@atlassian/jira-servicedesk-common/src/utils/browser-metric/index.tsx';
import { SpaStatePageReady } from '@atlassian/jira-spa-state-controller/src/components/main.tsx';
import AppDiscoveryProjectSettingsBreadcrumb from '../controllers/app-discovery-project-settings-breadcrumb';
import {
	APP_DISCOVERY_TTR_THRESHOLD,
	APP_DISCOVERY_TTI_THRESHOLD,
	appsDiscoveryPageLoad,
} from '../utils/performance-analytics';
import messages from './messages';

const ACTION_SUBJECT_ID = 'jsdSettingsAppDiscovery';
const MARKETPLACE_SOURCE = 'servicedesk-settings';

type Props = {
	isProjectSimplified: boolean;
	isJiraAdmin: boolean;
	baseUrl: string;
	projectKey: string;
	projectName: string;
	intl: IntlShape;
};

function AppDiscoverySettings(props: Props) {
	const {
		isProjectSimplified,
		baseUrl,
		projectKey,
		projectName,
		isJiraAdmin,
		intl: { formatMessage },
	} = props;

	return (
		<ContextualAnalyticsData sourceName="jsdSettingsAppDiscovery" sourceType={SCREEN}>
			<AppDiscoveryProjectSettingsBreadcrumb
				isProjectSimplified={isProjectSimplified}
				baseUrl={baseUrl}
				projectKey={projectKey}
				projectName={projectName}
			>
				<>
					<Page>
						<Grid>
							<GridColumn>
								<PageHeader
									breadcrumbs={
										<Box xcss={breadcrumbsWrapperStyles}>
											<Breadcrumbs
												// @ts-expect-error - Type '{ baseUrl: string; projectKey: string; projectName: string; }' is not assignable to type 'IntrinsicAttributes & Omit<Omit<Omit<{ TenantContextSubscriber: SubscriberComponent<State, BoundActions<State, Actions>, unknown>; onMount: (args_0: UIAnalyticsEvent) => void; }, keyof WithAnalyticsEventsProps>, "TenantContextSubscriber" | "onMount"> & Partial<...>, "TenantContextSubscriber" | "onMount">'.
												baseUrl={baseUrl}
												projectKey={projectKey}
												projectName={projectName}
											/>
										</Box>
									}
								>
									{formatMessage(
										fg('jsm_simplified_project_settings_navigation')
											? messages.headerNew
											: messages.header,
									)}
								</PageHeader>
								<p>{formatMessage(messages.subHeaderJsm)}</p>
							</GridColumn>
							<GridColumn>
								<AppTiles
									categoryTitle={formatMessage(messages.categoryAssetManagement)}
									marketingLabel="asset-management"
									isJiraAdmin={isJiraAdmin}
									actionSubjectId={ACTION_SUBJECT_ID}
									source={MARKETPLACE_SOURCE}
								/>
							</GridColumn>
							<GridColumn>
								<AppTiles
									categoryTitle={formatMessage(messages.categoryHelpCenterFormCustomization)}
									marketingLabel="in-app-customization"
									isJiraAdmin={isJiraAdmin}
									actionSubjectId={ACTION_SUBJECT_ID}
									source={MARKETPLACE_SOURCE}
								/>
							</GridColumn>
							<GridColumn>
								<AppTiles
									categoryTitle={formatMessage(messages.categoryAgentProductivity)}
									marketingLabel="jsd-in-app-agent-productivity"
									isJiraAdmin={isJiraAdmin}
									actionSubjectId={ACTION_SUBJECT_ID}
									source={MARKETPLACE_SOURCE}
								/>
							</GridColumn>
							<GridColumn>
								<AppTiles
									categoryTitle={formatMessage(messages.categoryReportingAnalytics)}
									marketingLabel="in-app-reporting"
									isJiraAdmin={isJiraAdmin}
									actionSubjectId={ACTION_SUBJECT_ID}
									source={MARKETPLACE_SOURCE}
								/>
							</GridColumn>
							<GridColumn>
								<AppTiles
									categoryTitle={formatMessage(messages.categoryAlertingIncidentManagement)}
									marketingLabel="in-app-incident-mgt"
									isJiraAdmin={isJiraAdmin}
									actionSubjectId={ACTION_SUBJECT_ID}
									source={MARKETPLACE_SOURCE}
								/>
							</GridColumn>
						</Grid>
					</Page>
				</>
			</AppDiscoveryProjectSettingsBreadcrumb>
			<FireScreenAnalytics attributes={{ isJiraAdmin }} />
			<JsmPageInteractiveEvent
				appName="jira-servicedesk-apps-discovery"
				ttrSlo={APP_DISCOVERY_TTR_THRESHOLD}
				ttiSlo={APP_DISCOVERY_TTI_THRESHOLD}
				// @ts-expect-error - TS2322 - Type 'PageLoadMetric' is not assignable to type 'BM3Metric'.
				metric={appsDiscoveryPageLoad}
			/>
			{/* SpaStatePageReady is used to indicate that the app can move to the "afterPaint" phase in react-loosely-lazy. */}
			{fg('jsm_simplified_project_settings_navigation') && <SpaStatePageReady />}
		</ContextualAnalyticsData>
	);
}

export default injectIntl(AppDiscoverySettings);

const breadcrumbsWrapperStyles = xcss({
	marginBottom: 'space.100',
	marginLeft: 'space.negative.050',
});
